'use client'

import * as React from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip } from '../Tooltip'

export interface BreadcrumbDropdownItem {
  label: string
  href: string
  description?: string
}

export interface BreadcrumbItem {
  label: string
  href?: string
  icon?: React.ReactNode
  dropdownItems?: BreadcrumbDropdownItem[]
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[]
  separator?: React.ReactNode
}

function BreadcrumbDropdown({ item, isLast }: { item: BreadcrumbItem; isLast: boolean }) {
  const [isOpen, setIsOpen] = React.useState(false)

  const baseClasses = isLast
    ? 'text-sm font-medium text-foreground'
    : 'text-sm text-muted-foreground hover:text-foreground transition-colors'

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(baseClasses, 'flex items-center gap-1 cursor-pointer')}
      >
        {item.icon}
        {item.label}
        <ChevronDown className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1.5 z-20 w-[260px]">
            <div className="bg-popover border border-border rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto overflow-x-hidden">
              {item.dropdownItems!.map((dropdownItem, i) => (
                <Tooltip
                  key={i}
                  side="right"
                  triggerClassName="flex w-full"
                  content={
                    <div>
                      <div className="font-medium">{dropdownItem.label}</div>
                      {dropdownItem.description && (
                        <div className="mt-1">{dropdownItem.description}</div>
                      )}
                    </div>
                  }
                >
                  <a
                    href={dropdownItem.href}
                    className="flex-1 min-w-0 px-3 py-2 text-sm hover:bg-accent transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="font-medium text-foreground truncate block">{dropdownItem.label}</span>
                    {dropdownItem.description && (
                      <span className="block text-xs text-muted-foreground mt-0.5 truncate">{dropdownItem.description}</span>
                    )}
                  </a>
                </Tooltip>
              ))}
              {item.dropdownItems!.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No items</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(
  ({ items, separator, className, ...props }, ref) => {
    const separatorElement = separator || <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />

    return (
      <nav ref={ref} aria-label="Breadcrumb" className={cn('flex items-center', className)} {...props}>
        <ol className="flex items-center gap-1.5">
          {items.map((item, index) => {
            const isLast = index === items.length - 1

            return (
              <li key={index} className="flex items-center gap-1.5">
                {index > 0 && separatorElement}
                {item.dropdownItems ? (
                  <BreadcrumbDropdown item={item} isLast={isLast} />
                ) : isLast ? (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    {item.icon}
                    {item.label}
                  </span>
                ) : item.href ? (
                  <a
                    href={item.href}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.icon}
                    {item.label}
                  </a>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    {item.icon}
                    {item.label}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    )
  }
)
Breadcrumbs.displayName = 'Breadcrumbs'

export { Breadcrumbs }
